import { useState, useEffect, useCallback, useRef } from "react";
import GameLayout from "../components/GameLayout";
import API from "../lib/api";
import { Play, Square, Download, Maximize, Minimize, Zap, Heart, Brain, TrendingUp, Pickaxe } from "lucide-react";
import { toast } from "sonner";
import GameSounds from "../lib/sounds";

const RESOURCE_COLORS = {
  Coal: { bg: "#3B3B3B", text: "#94A3B8", glow: "rgba(148,163,184,0.5)" },
  Iron: { bg: "#6B7280", text: "#D1D5DB", glow: "rgba(209,213,219,0.5)" },
  Silver: { bg: "#94A3B8", text: "#E2E8F0", glow: "rgba(226,232,240,0.6)" },
  Gold: { bg: "#F59E0B", text: "#FCD34D", glow: "rgba(252,211,77,0.7)" },
  Diamond: { bg: "#3B82F6", text: "#93C5FD", glow: "rgba(147,197,253,0.7)" },
  Mythril: { bg: "#B026FF", text: "#D8B4FE", glow: "rgba(216,180,254,0.8)" },
};

function FloatingParticle({ delay, color, x }) {
  return (
    <div className="absolute pointer-events-none" style={{
      left: `${x}%`, bottom: "20%",
      animation: `float-up 3s ease-out ${delay}s infinite`,
    }}>
      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color, boxShadow: `0 0 8px ${color}` }} />
    </div>
  );
}

function ResourcePopup({ name, qty }) {
  const res = RESOURCE_COLORS[name] || RESOURCE_COLORS.Coal;
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg animate-slide-up" style={{ backgroundColor: `${res.bg}30`, border: `1px solid ${res.bg}60` }}>
      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: res.bg, boxShadow: `0 0 6px ${res.glow}` }} />
      <span className="font-mono-game text-xs font-bold" style={{ color: res.text }}>+{qty} {name}</span>
    </div>
  );
}

export default function MiningPage() {
  const [session, setSession] = useState(null);
  const [worker, setWorker] = useState(null);
  const [allWorkers, setAllWorkers] = useState([]);
  const [selectedWorkerIds, setSelectedWorkerIds] = useState([]);
  const [assets, setAssets] = useState({});
  const [loading, setLoading] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [collected, setCollected] = useState(null);
  const [showSparks, setShowSparks] = useState(false);
  const [flyingResources, setFlyingResources] = useState([]);
  const gameRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [mRes, aRes] = await Promise.all([API.get("/mining/status"), API.get("/game-assets")]);
      setSession(mRes.data.session);
      const wks = mRes.data.workers || [];
      setAllWorkers(wks);
      setWorker(wks[0] || null);
      setAssets(aRes.data.assets || {});
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!session?.active) return;
    const interval = setInterval(() => {
      const start = new Date(session.last_collected).getTime();
      setElapsed(Math.min((Date.now() - start) / 3600000, 12));
    }, 1000);
    return () => clearInterval(interval);
  }, [session]);

  // Spark animation loop when active
  useEffect(() => {
    if (!session?.active) return;
    const interval = setInterval(() => {
      setShowSparks(true);
      setTimeout(() => setShowSparks(false), 600);
    }, 2000);
    return () => clearInterval(interval);
  }, [session]);

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      gameRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const startMining = async () => {
    setLoading("start");
    try {
      const ids = selectedWorkerIds.length > 0 ? selectedWorkerIds : allWorkers.filter(w => w.energy >= 10).map(w => w.worker_id);
      if (ids.length === 0) {
        toast.error("No workers with enough energy");
        setLoading("");
        return;
      }
      await API.post("/mining/start", { worker_ids: ids });
      toast.success(`${ids.length} worker(s) deployed!`);
      GameSounds.deploy();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(""); }
  };

  const collectMining = async () => {
    setLoading("collect");
    try {
      const { data } = await API.post("/mining/collect");
      setCollected(data.collected);
      // Create flying resource animations
      const flies = [];
      Object.entries(data.collected).forEach(([name, qty], idx) => {
        const res = RESOURCE_COLORS[name] || RESOURCE_COLORS.Coal;
        for (let i = 0; i < Math.min(qty, 5); i++) {
          flies.push({
            id: `${name}-${i}-${Date.now()}`,
            name, color: res.bg,
            startX: 50 + (Math.random() - 0.5) * 30,
            startY: 60 + Math.random() * 10,
            delay: idx * 200 + i * 100,
          });
        }
      });
      setFlyingResources(flies);
      setTimeout(() => { setFlyingResources([]); setCollected(null); }, 4000);
      // Show items collected + food cost summary
      const itemSummary = Object.entries(data.collected).map(([n, q]) => `+${q} ${n}`).join(", ");
      toast.success(`${itemSummary} | Food: -${data.food_cost}c`);
      // Show warnings
      if (data.warnings?.length > 0) {
        data.warnings.forEach(w => toast.error(w, { duration: 8000 }));
      }
      GameSounds.collect();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(""); }
  };

  const stopMining = async () => {
    setLoading("stop");
    try {
      await API.post("/mining/stop");
      toast.success("Workers recalled");
      GameSounds.recall();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(""); }
  };

  const isActive = session?.active;
  const deployedWorkers = allWorkers.filter(w => w.status === "deployed");
  const idleWorkers = allWorkers.filter(w => w.status === "idle");
  const progressPct = Math.min((elapsed / 12) * 100, 100);
  const hrs = Math.floor(elapsed);
  const mins = Math.floor((elapsed - hrs) * 60);
  const secs = Math.floor(((elapsed - hrs) * 60 - mins) * 60);
  // Exact estimate matching backend formula: sum of daily_rate * (elapsed/24)
  const totalDailyRate = deployedWorkers.reduce((sum, w) => sum + (w.daily_rate || 0), 0);
  const estResources = Math.max(0, Math.round(totalDailyRate * elapsed / 24));

  const toggleWorkerSelect = (wid) => {
    setSelectedWorkerIds(prev => prev.includes(wid) ? prev.filter(id => id !== wid) : [...prev, wid]);
  };

  const caveBg = assets.cave_bg?.image_url || "https://static.prod-images.emergentagent.com/jobs/bf9e0d20-757d-4e16-8aee-b054e0c78697/images/9ff327805df8bb7007aaa05b60bb28f3aec867c046d8fea9b6579e84c4c6e114.png";
  const workerImg = assets.worker_character?.image_url || "https://static.prod-images.emergentagent.com/jobs/bf9e0d20-757d-4e16-8aee-b054e0c78697/images/8d6e3f7efeac3bec6e15aa0d3009ebcea428e7a2b007276162970a447bd2d82c.png";
  const diggingImg = assets.workers_digging?.image_url || "https://static.prod-images.emergentagent.com/jobs/bf9e0d20-757d-4e16-8aee-b054e0c78697/images/68baf6fc6f8e961f4244d55fd4e95b1699968cfddb5cdcb7c531adb709772362.png";

  const gameContent = (
    <div ref={gameRef} className={`relative ${isFullscreen ? "w-screen h-screen" : "w-full rounded-2xl overflow-hidden"} bg-[#0B0E14]`} data-testid="mining-game-scene" style={{ minHeight: isFullscreen ? "100vh" : "70vh" }}>
      {/* Cave Background */}
      <div className="absolute inset-0">
        <img src={isActive ? diggingImg : caveBg} alt="Cave" className="w-full h-full object-cover" style={{ filter: isActive ? "brightness(0.7) saturate(1.2)" : "brightness(0.5) saturate(0.8)" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14] via-transparent to-[#0B0E14]/40" />
        {isActive && <div className="absolute inset-0 bg-[#00F0FF]/[0.03] animate-mining-pulse" />}
      </div>

      {/* Floating Particles when active */}
      {isActive && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[15, 25, 40, 55, 65, 75, 85].map((x, i) => (
            <FloatingParticle key={`p-${x}-${i}`} delay={i * 0.5} color={["#00F0FF", "#FFD600", "#B026FF", "#10B981"][i % 4]} x={x} />
          ))}
        </div>
      )}

      {/* Spark effects on character */}
      {showSparks && isActive && (
        <div className="absolute left-1/2 bottom-[28%] -translate-x-1/2 pointer-events-none z-20">
          <div className="relative">
            {[0, 1, 2, 3, 4, 5].map(i => (
              <div key={`spark-${i}`} className="absolute w-1.5 h-1.5 bg-[#FFD600] rounded-full" style={{
                transform: `rotate(${i * 60}deg) translateY(-${12 + Math.random() * 20}px)`,
                animation: "spark-fly 0.6s ease-out forwards",
                opacity: 0.8,
                boxShadow: "0 0 6px #FFD600",
              }} />
            ))}
          </div>
        </div>
      )}

      {/* Worker Character */}
      <div className={`absolute left-1/2 bottom-[10%] -translate-x-1/2 z-10 transition-all duration-300 ${isActive ? "animate-worker-dig" : ""}`}>
        <img src={workerImg} alt="Worker" className="w-48 h-48 sm:w-64 sm:h-64 object-contain drop-shadow-[0_0_20px_rgba(0,240,255,0.4)]" data-testid="game-worker-character" />
        {isActive && (
          <div className="absolute -top-2 left-1/2 -translate-x-1/2">
            <div className="flex items-center gap-1 px-2 py-0.5 bg-[#0B0E14]/80 rounded-full border border-[#00F0FF]/30">
              <div className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" />
              <span className="text-[10px] font-mono-game text-[#00F0FF]">DIGGING</span>
            </div>
          </div>
        )}
      </div>

      {/* Collected Resources Popup */}
      {collected && (
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-1" data-testid="collected-resources-popup">
          {Object.entries(collected).map(([name, qty]) => (
            <ResourcePopup key={name} name={name} qty={qty} />
          ))}
        </div>
      )}

      {/* Flying Resource Drop Animations */}
      {flyingResources.map((fr) => (
        <div key={fr.id} className="absolute z-30 pointer-events-none" style={{
          left: `${fr.startX}%`, top: `${fr.startY}%`,
          animation: `resource-fly-up 2s ease-out ${fr.delay}ms forwards`,
        }}>
          <div className="relative">
            <div className="w-4 h-4 rounded-full animate-pulse" style={{
              backgroundColor: fr.color,
              boxShadow: `0 0 12px ${fr.color}, 0 0 24px ${fr.color}50`,
            }} />
            <span className="absolute -top-4 left-1/2 -translate-x-1/2 text-[10px] font-mono-game font-bold whitespace-nowrap" style={{ color: fr.color, textShadow: `0 0 8px ${fr.color}` }}>
              +1
            </span>
          </div>
        </div>
      ))}

      {/* HUD Overlay - Top Bar */}
      <div className="absolute top-0 left-0 right-0 z-20 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {!isFullscreen && (
              <div className="flex items-center gap-2 px-3 py-2 bg-[#0B0E14]/80 backdrop-blur-sm rounded-lg border border-[#2A3241]/60">
                <div className={`w-3 h-3 rounded-full ${isActive ? "bg-[#10B981] animate-pulse" : "bg-[#64748B]"}`} />
                <span className="font-mono-game text-sm text-[#F8FAFC]">{isActive ? "ACTIVE" : "IDLE"}</span>
              </div>
            )}
            {isActive && (
              <div className="px-3 py-2 bg-[#0B0E14]/80 backdrop-blur-sm rounded-lg border border-[#00F0FF]/30">
                <span className="font-mono-game text-sm text-[#00F0FF]" data-testid="mining-timer">
                  {String(hrs).padStart(2, "0")}:{String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}
                </span>
              </div>
            )}
          </div>
          <button onClick={toggleFullscreen} data-testid="fullscreen-toggle" className="p-2 bg-[#0B0E14]/80 backdrop-blur-sm rounded-lg border border-[#2A3241]/60 text-[#94A3B8] hover:text-[#F8FAFC] transition-colors">
            {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* HUD - Worker Stats (right side) */}
      {worker && (
        <div className="absolute top-16 right-4 z-20 space-y-2 w-36" data-testid="hud-worker-stats">
          {[
            { icon: Zap, label: "Energy", val: worker.energy, max: worker.max_energy, color: worker.energy < 50 ? "#EF4444" : "#00F0FF" },
            { icon: Heart, label: "Stamina", val: worker.hunger, max: 100, color: worker.hunger < 50 ? "#EF4444" : "#FFD600" },
            { icon: TrendingUp, label: "Efficiency", val: Math.round((worker.efficiency || 1) * 100), max: 100, color: (worker.efficiency || 1) < 0.5 ? "#EF4444" : "#10B981" },
          ].map(({ icon: Icon, label, val, max, color }) => (
            <div key={label} className="flex items-center gap-2 px-2 py-1.5 bg-[#0B0E14]/80 backdrop-blur-sm rounded-lg border border-[#2A3241]/40">
              <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color }} />
              <div className="flex-1 min-w-0">
                <div className="h-1.5 bg-[#1C212E] rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(val / max) * 100}%`, backgroundColor: color }} />
                </div>
              </div>
              <span className="font-mono-game text-[10px] w-7 text-right" style={{ color: val < max * 0.5 ? "#EF4444" : "#94A3B8" }}>{val}</span>
            </div>
          ))}
        </div>
      )}

      {/* HUD - Equipment (left side) */}
      {worker?.equipment && (
        <div className="absolute top-16 left-4 z-20 space-y-1" data-testid="hud-equipment">
          {Object.entries(worker.equipment).map(([slot, item]) => (
            item !== "None" && (
              <div key={slot} className="flex items-center gap-2 px-2 py-1 bg-[#0B0E14]/80 backdrop-blur-sm rounded-lg border border-[#FFD600]/20">
                <Pickaxe className="w-3 h-3 text-[#FFD600]" />
                <span className="text-[10px] font-mono-game text-[#FFD600]">{item}</span>
              </div>
            )
          ))}
        </div>
      )}

      {/* HUD - Bottom Progress & Actions */}
      <div className="absolute bottom-0 left-0 right-0 z-20 p-4">
        {/* Progress Bar */}
        {isActive && (
          <div className="mb-3 px-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-mono-game text-[#64748B] tracking-wider">EXTRACTION PROGRESS (12H MAX)</span>
              <span className="text-[10px] font-mono-game text-[#94A3B8]">~{estResources} resources</span>
            </div>
            <div className="h-3 bg-[#0B0E14]/80 backdrop-blur-sm rounded-full overflow-hidden border border-[#2A3241]/40" data-testid="game-progress-bar">
              <div className="h-full rounded-full transition-all duration-1000 relative" style={{
                width: `${progressPct}%`,
                background: "linear-gradient(90deg, #00F0FF, #B026FF, #FFD600)",
              }}>
                <div className="absolute right-0 top-0 bottom-0 w-3 bg-white/40 rounded-full animate-pulse" />
              </div>
            </div>
          </div>
        )}

        {/* Worker Selection (when idle) */}
        {!isActive && allWorkers.length > 1 && (
          <div className="mb-3 px-2">
            <p className="text-[10px] font-mono-game text-[#64748B] tracking-wider mb-2">SELECT WORKERS TO DEPLOY ({selectedWorkerIds.length || allWorkers.filter(w => w.energy >= 10).length}/{allWorkers.length})</p>
            <div className="flex flex-wrap gap-2">
              {allWorkers.map(w => {
                const sel = selectedWorkerIds.length === 0 ? w.energy >= 10 : selectedWorkerIds.includes(w.worker_id);
                const lowEnergy = w.energy < 10;
                return (
                  <button key={w.worker_id} onClick={() => !lowEnergy && toggleWorkerSelect(w.worker_id)} disabled={lowEnergy}
                    data-testid={`select-worker-${w.worker_id}`}
                    className={`px-3 py-1.5 rounded-lg text-[10px] font-mono-game border transition-all ${sel && !lowEnergy ? "border-[#00F0FF] bg-[#00F0FF]/20 text-[#00F0FF]" : lowEnergy ? "border-[#EF4444]/30 bg-[#EF4444]/10 text-[#EF4444] opacity-50 cursor-not-allowed" : "border-[#2A3241] bg-[#0B0E14]/80 text-[#94A3B8] hover:border-[#00F0FF]/50"}`}>
                    {w.name} ({w.energy}E)
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Deployed Workers Count */}
        {isActive && deployedWorkers.length > 0 && (
          <div className="mb-3 px-2">
            <p className="text-[10px] font-mono-game text-[#10B981] tracking-wider">{deployedWorkers.length} WORKER{deployedWorkers.length > 1 ? "S" : ""} DEPLOYED</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-center gap-3">
          {!isActive ? (
            <button onClick={startMining} disabled={loading === "start"} data-testid="mine-start-btn"
              className="flex items-center gap-2 px-8 py-3 text-sm font-bold uppercase tracking-widest bg-[#00F0FF] text-[#0B0E14] rounded-xl hover:bg-[#00F0FF]/90 disabled:opacity-50 shadow-[0_0_30px_rgba(0,240,255,0.5)] transition-all hover:scale-105">
              <Play className="w-5 h-5" /> {loading === "start" ? "Deploying..." : `Deploy ${selectedWorkerIds.length || allWorkers.filter(w => w.energy >= 10).length} Worker(s)`}
            </button>
          ) : (
            <>
              <button onClick={collectMining} disabled={loading === "collect"} data-testid="mine-collect-btn"
                className="flex items-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-widest bg-[#10B981] text-white rounded-xl hover:bg-[#10B981]/90 disabled:opacity-50 shadow-[0_0_20px_rgba(16,185,129,0.4)] transition-all hover:scale-105">
                <Download className="w-4 h-4" /> {loading === "collect" ? "Collecting..." : "Collect"}
              </button>
              <button onClick={stopMining} disabled={loading === "stop"} data-testid="mine-stop-btn"
                className="flex items-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-widest bg-transparent border-2 border-[#EF4444] text-[#EF4444] rounded-xl hover:bg-[#EF4444]/10 disabled:opacity-50 transition-all">
                <Square className="w-4 h-4" /> Recall
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <GameLayout title="Extraction Zone">
      <div className="space-y-4" data-testid="mining-page">
        {gameContent}

        {/* Resource Tiers Legend */}
        <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-4">
          <p className="text-xs font-mono-game text-[#64748B] tracking-[0.15em] uppercase mb-3">Resource Tiers</p>
          <div className="flex flex-wrap gap-3">
            {Object.entries(RESOURCE_COLORS).map(([name, { bg, text }]) => (
              <div key={name} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#0B0E14] border border-[#2A3241]/30">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: bg, boxShadow: `0 0 4px ${bg}` }} />
                <span className="text-xs font-mono-game" style={{ color: text }}>{name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </GameLayout>
  );
}
